import React, { useEffect, useRef, useState } from 'react';
import { attachHls, canPlayH264, observeOnScreen } from '../../utils/playerEngine';

/** Muted, looping ambient video (hero backgrounds, previews). HLS when available, MP4 otherwise.
 *  Loads only once on screen; pauses when scrolled away or the tab is hidden. */
export const BackgroundVideo = ({ hlsUrl = '', mp4Url = '', poster = '', className = '', fit = 'cover', onReady, ...rest }) => {
  const ref = useRef(null);
  const [useMp4, setUseMp4] = useState(!hlsUrl);
  const [onScreen, setOnScreen] = useState(false);
  const [seen, setSeen] = useState(false);

  useEffect(() => { setUseMp4(!hlsUrl); }, [hlsUrl, mp4Url]);
  useEffect(() => observeOnScreen(ref.current, setOnScreen), []);

  useEffect(() => {
    if (onScreen) setSeen(true);
    const v = ref.current;
    if (!v || !seen) return;
    if (onScreen) v.play().catch(() => {}); else v.pause();
  }, [onScreen, seen]);

  useEffect(() => {
    const v = ref.current;
    if (!v || !seen) return undefined;
    let detach = null;
    const play = () => { if (!document.hidden) v.play().catch(() => {}); };
    if (!useMp4 && hlsUrl) {
      detach = attachHls(v, hlsUrl, { onFatal: () => setUseMp4(true), onReady: () => { onReady?.(); play(); } });
    } else if (mp4Url) {
      v.src = mp4Url;
      const ready = () => { onReady?.(); play(); };
      v.addEventListener('loadeddata', ready, { once: true });
      play(); // kicks off loading — with preload="none" nothing loads until play() is called
      detach = () => { v.removeEventListener('loadeddata', ready); v.removeAttribute('src'); v.load(); };
    }
    return () => detach?.();
  }, [useMp4, hlsUrl, mp4Url, seen]); // eslint-disable-line react-hooks/exhaustive-deps

  if ((!hlsUrl && !mp4Url) || !canPlayH264()) return null;
  return (
    <video
      ref={ref}
      className={`w-full h-full object-${fit} ${className}`}
      poster={poster || undefined}
      muted
      loop
      playsInline
      preload={seen ? 'auto' : 'none'}
      disablePictureInPicture
      aria-hidden="true"
      {...rest}
    />
  );
};

export default BackgroundVideo;
